<script setup lang="ts">
import {computed, ref, watch} from 'vue';
import {Icon} from '@iconify/vue';
import {injectScoreEditContext} from "@/components/musicxml-preview/editContext.ts";
import {composeChordSymbol, mapKindToSymbol, parseChordSymbol} from "@/composables/musicXmlDom";
import {watchDebounced} from "@vueuse/core";
import Collapsible from "@/components/generic/Collapsible.vue";


const {

  currentChord,

  selectedMeasureIndex: measureIndex,
  selectedMeasure: measureNumber,
  saveChord,
  removeChord,
  selectNextMeasure,
} = injectScoreEditContext();

const chordRoot = ref('C');
const chordQuality = ref('');
// Basston eines Slash-Akkords (`C/E`): Stufe + eigenes Vorzeichen, damit auch B♭-/E♭-Bässe gehen.
const bassStep = ref('');
const bassAccidental = ref('');
const chordBass = computed(() => bassStep.value ? bassStep.value + bassAccidental.value : '');

const roots = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
const accidentals = ['', '#', 'b'];
const qualities = [
  {value: '', label: 'Dur', example: 'C'},
  {value: 'm', label: 'Moll', example: 'Cm'},
  {value: '7', label: 'Dur 7', example: 'C7'},
  {value: 'maj7', label: 'Maj 7', example: 'Cmaj7'},
  {value: 'm7', label: 'Moll 7', example: 'Cm7'},
  {value: 'dim', label: 'Vermindert', example: 'Cdim'},
  {value: 'aug', label: 'Übermäßig', example: 'Caug'},
  {value: 'sus2', label: 'Sus 2', example: 'Csus2'},
  {value: 'sus4', label: 'Sus 4', example: 'Csus4'},
  {value: '6', label: 'Dur 6', example: 'C6'},
  {value: 'm6', label: 'Moll 6', example: 'Cm6'},
  {value: '9', label: 'Dur 9', example: 'C9'},
];

const selectedAccidental = ref('');

const fullChord = computed(() => composeChordSymbol({
  root: chordRoot.value,
  accidental: selectedAccidental.value,
  quality: chordQuality.value,
  bass: chordBass.value,
}));


// Akkord-Parsing über das Composable (eine Quelle statt duplizierter Regex-Logik, die driften
// könnte); die MusicXML-`alter`/`kind`-Werte werden hier nur auf die Symbol-Refs abgebildet.

function alterToAccidental(alter: string): string {
  return '1' === alter ? '#' : '-1' === alter ? 'b' : '';
}

function parseChord(chord: string) {
  if (!chord) return;
  const parsed = parseChordSymbol(chord);
  chordRoot.value = parsed.root;
  selectedAccidental.value = alterToAccidental(parsed.alter);
  const symbol = mapKindToSymbol(parsed.kind);
  chordQuality.value = qualities.some(q => q.value === symbol) ? symbol : '';
  bassStep.value = parsed.bass?.step ?? '';
  bassAccidental.value = parsed.bass ? alterToAccidental(parsed.bass.alter) : '';
}

function clearBass() {
  bassStep.value = '';
  bassAccidental.value = '';
}

function setChord(c: string) {
  // Grundton setzen – Speichern + Sprung zum nächsten Takt übernimmt watchDebounced(fullChord).
  chordRoot.value = c;
}

function handleSave() {
  if (null === measureNumber.value) return;
  // Rückkopplung vermeiden: selectNextMeasure lädt im Folgetakt dessen Akkord (currentChord) →
  // parseChord ändert fullChord → handleSave. Nur speichern + weiterspringen, wenn der Akkord vom
  // geladenen Takt-Inhalt abweicht; sonst würde der Auto-Save durchs ganze Stück rasen.
  if (fullChord.value === (currentChord.value ?? '')) return;
  saveChord({
    measureNumber: measureNumber.value,
    measureIndex: measureIndex.value ?? undefined,
    chord: fullChord.value,
  });
  selectNextMeasure();
}

function handleRemove() {
  if (null !== measureNumber.value) {
    removeChord(measureNumber.value, measureIndex.value ?? undefined);
  }
}

// Quick select common chords
const quickChords = ['G', 'C', 'F', 'Bb', 'G7', 'C7', 'D7', 'F7'];


function selectQuickChord(chord: string) {
  parseChord(chord);
}

watchDebounced(() => fullChord.value, handleSave, {debounce: 30})
// Parse existing chord when the selected measure / current chord changes
watch(() => currentChord.value, (chord) => {
  if (chord) {
    parseChord(chord);
  } else {
    // Leerer Takt → Felder leeren (chordRoot='' statt Default 'C', damit fullChord='' dem leeren
    // currentChord entspricht und der handleSave-Guard greift – sonst würde der Default 'C' jeden
    // leeren Folgetakt befüllen).
    chordRoot.value = '';
    selectedAccidental.value = '';
    chordQuality.value = '';
    clearBass();
  }
}, {immediate: true});
// Pfeiltasten (Takt-Navigation) laufen jetzt zentral über MusicXmlPreview (globale Handler) –
// hier keine eigenen ArrowLeft/Right-Listener mehr (verhinderte Doppelauslösung).
</script>

<template>
  <Transition
      enter-active-class="animate-fade animate-duration-200 animate-ease-out"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
  >
  <div
      v-if="null !== measureNumber"
      class="text-sm flex items-center justify-center p-0"
  >
  <div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden ring-1 ring-black/5">

  <!-- Header -->
  <div class="flex items-center justify-between p-5 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-indigo-50">
    <div>
      <h2 class="text-xl font-bold text-gray-800 flex items-center gap-3">
        <div class="p-2 bg-purple-100 text-purple-600 rounded-lg">
          <Icon icon="game-icons:guitar-head" class="text-lg"/>
        </div>
        Akkord bearbeiten
      </h2>
      <p class="text-sm text-gray-600 mt-1">Takt {{ measureNumber }}</p>
    </div>
  </div>

  <!-- Content -->
  <div class="p-1 gap-1 gap-y-6">

  <div class="flex gap-2 mb-2">
    <!-- Chord Preview -->
    <div class="text-center grid items-center h-16 aspect-square bg-gradient-to-r from-purple-100 to-indigo-100 rounded-xl w-16">
      <span class="text-2xl  font-bold text-purple-800">{{ fullChord || '–' }}</span>
    </div>
    <!-- Quick Select -->
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-2">Schnellauswahl</label>
      <div class="flex flex-wrap gap-1">
        <button
            v-for="chord in quickChords"
            :key="chord"
            @click="selectQuickChord(chord)"
            class="text-center py-1.5 text-sm font-medium w-10 h-10 rounded-lg border-gray-400 border transition-colors"
            :class="fullChord === chord
                  ? 'bg-purple-100 border-purple-300 text-purple-700'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'"
        >
          {{ chord }}
        </button>
      </div>
    </div>
  </div>

  <!-- Root Note -->
  <Collapsible variant="default" class="text-sm text-blue-500" :is-open="false">
  <template #header>
    <h5 class="text-lg">Alle Akkorde</h5>
  </template>
  <template #default>
  <div>
    <label class="block text-sm font-medium text-gray-700 mb-2">Grundton</label>
    <div class="flex gap-1">
      <div class="flex-1 flex gap-1">
        <button
            v-for="root in roots"
            :key="root"
            @click="setChord(root)"
            class="flex-1 py-2 text-sm font-medium rounded-lg border transition-colors"
            :class="chordRoot === root
                    ? 'bg-purple-500 border-purple-500 text-white'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'"
        >
          {{ root }}
        </button>
      </div>
      <div class="flex gap-1">
        <button
            v-for="acc in accidentals"
            :key="acc || 'natural'"
            @click="selectedAccidental = acc"
            class="w-10 py-2 text-sm font-medium rounded-lg border transition-colors"
            :class="selectedAccidental === acc
                    ? 'bg-purple-500 border-purple-500 text-white'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'"
        >
          {{ acc || '♮' }}
        </button>
      </div>
    </div>
  </div>

  <hr class="my-2 opacity-40"/>
  <!-- Chord Quality -->
  <div>
    <label class="block text-sm font-medium text-gray-700 mb-2">Akkordtyp</label>
    <div class="grid grid-cols-4 gap-2">
      <button
          v-for="quality in qualities"
          :key="quality.value"
          @click="chordQuality = quality.value"
          class="py-2 px-2 text-xs font-medium rounded-lg border transition-colors text-center"
          :class="chordQuality === quality.value
                  ? 'bg-purple-500 border-purple-500 text-white'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'"
      >
        <div>{{ quality.label }}</div>
      </button>
    </div>
  </div>
  <hr class="my-2 opacity-40"/>
  <!-- Bass Note (optional) -->
  <div>
    <label class="block text-sm font-medium text-gray-700 mb-2">Basston (optional)</label>
    <div class="flex gap-1">
      <button
          @click="clearBass()"
          class="px-3 py-2 text-sm font-medium rounded-lg border transition-colors"
          :class="!chordBass
                  ? 'bg-purple-500 border-purple-500 text-white'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'"
      >
        Keiner
      </button>
      <button
          v-for="root in roots"
          :key="'bass-' + root"
          @click="bassStep = root"
          class="flex-1 py-2 text-sm font-medium rounded-lg border transition-colors"
          :class="bassStep === root
                  ? 'bg-purple-500 border-purple-500 text-white'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'"
      >
        {{ root }}
      </button>
      <button
          v-for="acc in accidentals"
          :key="'bass-acc-' + (acc || 'natural')"
          @click="bassAccidental = acc"
          class="w-10 py-2 text-sm font-medium rounded-lg border transition-colors"
          :class="bassAccidental === acc
                  ? 'bg-purple-500 border-purple-500 text-white'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'"
      >
        {{ acc || '♮' }}
      </button>

    </div
    >

  </div>
  </template>
  </Collapsible>
  </div>

  <!-- Footer -->
  <div class="flex items-center justify-between gap-3 p-5 border-t border-gray-100 bg-gray-50/50">
    <button
        v-if="currentChord"
        @click="handleRemove"
        class="px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-2"
    >
      <Icon icon="fa6-solid:trash"/>
      Entfernen
    </button>
    <div v-else></div>
  </div>
  </div>
  </div>
  </Transition>
</template>
